import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useOfferApprovals } from "../../../../../data/useCallQueue";
import useWilmaUsers from "../../../../../data/useWilmaUsers";
import { IPendingApprovalRow } from "../../../../../models/CallQueue";
import { vehicleTitle } from "../../../../../utils/callQueue";
import PriceFormat from "../../../../../utils/priceFormat";
import RegPlate from "../../../../shared/RegPlate";
import Pill from "../Pill";

function ApprovalRow({ row, requester }: { row: IPendingApprovalRow; requester?: string }) {
  const { t } = useTranslation("leads");
  const { decide } = useOfferApprovals(true, false, "PendingApprovalsPanel");
  const [approvedPrice, setApprovedPrice] = useState(row.requestedPrice.toFixed(2));
  const [declineReason, setDeclineReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (approve: boolean) => {
    setError(null);
    const res = await decide.mutateAsync({
      approvalId: row.id,
      body: approve ? { approve, approvedPrice: Number(approvedPrice) } : { approve, declineReason },
    });
    if (!res.success) setError(res.message);
  };

  const over = row.requestedPrice - row.max;

  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <RegPlate vrm={row.vrm} />
        <span className="text-[13px] font-semibold text-gray-900">{vehicleTitle(null, row.make, row.model)}</span>
        <Pill tone="red">{PriceFormat(over)} {t("over max")}</Pill>
        <Link to={`/bookings/${row.leadId}`} className="text-xs font-medium text-primary-700 underline">{t("Open lead")}</Link>
      </div>
      <p className="text-xs text-gray-700">
        {t("{{name}} asks for {{price}} (max {{max}}, quoted {{quoted}}).", {
          name: requester || t("An agent"),
          price: PriceFormat(row.requestedPrice),
          max: PriceFormat(row.max),
          quoted: PriceFormat(row.quotedPrice),
        })}
        {row.note ? ` “${row.note}”` : ""}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        {!declining ? (
          <>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-700">
              {t("Approve up to")}
              <span className="flex min-h-[44px] w-32 items-center rounded-md border border-gray-300 bg-white px-2">
                <span className="text-gray-500">£</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={approvedPrice}
                  onChange={(e) => setApprovedPrice(e.target.value.replace(/[^\d.]/g, ""))}
                  className="w-full border-0 bg-transparent py-0 pl-1 text-sm focus:ring-0"
                />
              </span>
            </label>
            <button
              type="button"
              disabled={decide.isLoading}
              onClick={() => submit(true)}
              className="min-h-[44px] rounded-md border border-green-700 bg-green-700 px-3.5 text-[13px] font-semibold text-white disabled:opacity-50"
            >
              {t("Approve")}
            </button>
            <button
              type="button"
              onClick={() => setDeclining(true)}
              className="min-h-[44px] rounded-md border border-gray-300 bg-white px-3.5 text-[13px] font-medium text-gray-700 hover:bg-gray-50"
            >
              {t("Decline")}
            </button>
          </>
        ) : (
          <>
            <label className="flex flex-1 basis-60 flex-col gap-1 text-xs font-medium text-gray-700">
              {t("Why not?")}
              <input
                type="text"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="min-h-[44px] rounded-md border-gray-300 text-sm"
              />
            </label>
            <button
              type="button"
              disabled={decide.isLoading || !declineReason.trim()}
              onClick={() => submit(false)}
              className="min-h-[44px] rounded-md border border-red-600 bg-red-600 px-3.5 text-[13px] font-semibold text-white disabled:opacity-50"
            >
              {t("Decline")}
            </button>
            <button type="button" onClick={() => setDeclining(false)} className="min-h-[44px] px-2 text-[13px] text-gray-600 underline">
              {t("Cancel")}
            </button>
          </>
        )}
      </div>
      {error && <p className="text-xs text-red-700" role="alert">{error}</p>}
    </li>
  );
}

// On the Manager Assistance tab, for agents who can approve offers: the offers waiting on them.
export default function PendingApprovalsPanel() {
  const { t } = useTranslation("leads");
  const { pending } = useOfferApprovals(true, true, "PendingApprovalsPanel");
  const { wilmaUsers } = useWilmaUsers();

  const names = useMemo(() => {
    const map = new Map<string, string>();
    (wilmaUsers.data?.data ?? []).forEach((user) => map.set(user.id, user.fullname || user.forename || ""));
    return map;
  }, [wilmaUsers.data]);

  // Not an approver: the API refuses, and there's nothing to show.
  if (!pending.data?.success) return null;
  const rows = pending.data.data ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm">
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-800">{t("Offers waiting for approval")}</h3>
      </div>
      <ul className="divide-y divide-gray-200">
        {rows.map((row) => (
          <ApprovalRow key={row.id} row={row} requester={names.get(row.requestedByUserId)} />
        ))}
      </ul>
    </section>
  );
}
